import { EventTrackingCode, TrackingCode } from "../models/tracking-code.model";

/**
 * Injects facebook pixel tracking scripts if fbq is not defined already
 * Note, after injection facebook tracking scripts are stored in window.fbq
 * 
 * @returns
 */
export const injectFacebookPixelTrackingScripts = (trackingCodes: EventTrackingCode[]) => {
    if (window.fbq) return window.fbq;
    // @ts-ignore
    !function (f, b, e, v, n, t, s) {
        if (f.fbq) return; n = f.fbq = function () {
            n.callMethod ?
                n.callMethod.apply(n, arguments) : n.queue.push(arguments)
        };
        // @ts-ignore
        if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0';
        n.queue = []; t = b.createElement(e); t.async = !0;
        t.src = v; s = b.getElementsByTagName(e)[0];
        s.parentNode.insertBefore(t, s)
    }(window, document, 'script',
        'https://connect.facebook.net/en_US/fbevents.js');
    trackingCodes.forEach(({ trackingId }) => fbq('init', trackingId));
    return window.fbq;
}

/**
 * Injects tiktok pixel tracking scripts if ttq is not defined already
 * Note, after injection tiktok tracking scripts are stored in window.ttq
 * 
 * @returns
 */
export const injectTikTokPixelTrackingScripts = (trackingCodes: EventTrackingCode[]) => {
    if (window.ttq) return window.ttq;
    // @ts-ignore
    !function (w, d, t) {
        // @ts-ignore
        w.TiktokAnalyticsObject = t; var ttq = w[t] = w[t] || [];
        // @ts-ignore
        ttq.methods = ["page", "track", "identify", "instances", "debug", "on", "off", "once", "ready", "alias", "group", "enableCookie", "disableCookie"], ttq.setAndDefer = function (t, e) { t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))) } };
        for (var i = 0; i < ttq.methods.length; i++)ttq.setAndDefer(ttq, ttq.methods[i]);
        // @ts-ignore
        ttq.instance = function (t) {
            for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)ttq.setAndDefer(e, ttq.methods[n]); return e
            // @ts-ignore
        }, ttq.load = function (e, n) { var i = "https://analytics.tiktok.com/i18n/pixel/events.js"; ttq._i = ttq._i || {}, ttq._i[e] = [], ttq._i[e]._u = i, ttq._t = ttq._t || {}, ttq._t[e] = +new Date, ttq._o = ttq._o || {}, ttq._o[e] = n || {}; n = document.createElement("script"); n.type = "text/javascript", n.async = !0, n.src = i + "?sdkid=" + e + "&lib=" + t; e = document.getElementsByTagName("script")[0]; e.parentNode.insertBefore(n, e) };
    }(window, document, 'ttq');
    trackingCodes.forEach(({ trackingId }) => ttq.load('init', trackingId));
    return window.ttq;
}


/**
 * Defines window.gtag
 * 
 * @param trackingIds
 * @param gaClientId
 */
export const injectGoogleGATrackingScripts = (trackingCodes: EventTrackingCode[], gaClientId: string = '') => {
    const gtagSrc = document.createElement('script');
    gtagSrc.src = `https://www.googletagmanager.com/gtag/js`;
    document.head.appendChild(gtagSrc);

    const gtagScript = document.createElement('script');
    gtagScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag() { dataLayer.push(arguments); }
        gtag('js', new Date());`;
    trackingCodes.forEach(({ trackingId }) =>
        gtagScript.innerHTML += "gtag('config', '" + trackingId + "', {'cookie_flags': 'SameSite=None;Secure', 'client_id': " + gaClientId + " });"
    );
    document.head.appendChild(gtagScript);
}

/**
 * Initialises tracking for Facebook, TikTok and Google Analytics
 * Will only call init or load if this is the first time the tracking scripts are being injected 
 *
 * @param data
 * @returns
 */
export const intialiseTracking = (data: TrackingCode) => {
    if (!data) return;

    const { facebookPixelEventTrackingList, tikTokPixelEventTrackingList, googleAnalyticsEventTrackingList } = data;

    if (facebookPixelEventTrackingList?.length) {
        injectFacebookPixelTrackingScripts(facebookPixelEventTrackingList);
    }
    if (tikTokPixelEventTrackingList?.length) {
        injectTikTokPixelTrackingScripts(tikTokPixelEventTrackingList)
    }
    if (googleAnalyticsEventTrackingList?.length) {
        const urlParams = new URLSearchParams(window.location.search);
        const gaClientId = urlParams.get('gaClientId');

        injectGoogleGATrackingScripts(googleAnalyticsEventTrackingList, gaClientId)
    }
}

export const getEventTrackingCodesByAction = (data: EventTrackingCode[], action: string, prefix?: string) => {
    let actions = data.filter(({ trackActions }) => trackActions.includes(action));
    if (prefix) {
        actions = actions.filter(({ trackingId }) => trackingId.startsWith(prefix));
    }
    return actions;
}

/**
 * Waits for Facebook pixel to load, and then runs a callback
 *
 * @param callback
 */
export const waitForFbq = (callback: () => void) => {
    if (fbq) callback();
    var script = document.querySelector('#facebookPixelScripts');

    if (script) { 
        script.addEventListener('load', function () {
            callback();
        });
    }
}

/**
 * Waits for Tiktok pixel to load, and then runs a callback
 *
 * @param callback
 */
export const waitForTtq = (callback: () => void) => {
    if (ttq) callback();
    var script = document.querySelector('#tiktokPixelScripts');

    if (script) {
        script.addEventListener('load', function () {
            callback();
        });
    }
}


/**
 * Waits for gTag to load, and then runs a callback
 *
 * @param callback
 */
export const waitForGtag = (callback: () => void) => {
    if (gtag) callback();
    var script = document.querySelector('#gtagScripts');

    if (script) {
        script.addEventListener('load', function () {
            callback();
        });
    }
}